import type { LocationQuery } from 'vue-router';

import { AnalyticsComparisonMode } from '@shared/api/graphql/gql/graphql';
import { Temporal, parsePlainDate } from '@shared/lib/date-time';

import { queryToRecord } from './query-to-record';

const analyticsPeriodPresets = [
  'current-month',
  'custom',
  'last-12-months',
  'last-30-days',
  'last-3-months',
  'last-6-months',
  'last-7-days',
  'previous-month',
  'today',
  'year-to-date',
] as const;

export type AnalyticsPeriodPreset = (typeof analyticsPeriodPresets)[number];

export interface AnalyticsFilters {
  /** База сравнения: `'year'` — те же даты год назад, отсутствие — прошлый период. */
  compare?: 'year';
  dateFrom: string;
  dateTo: string;
  locationId: string;
  preset: AnalyticsPeriodPreset;
}

const isValidIsoDate = (value: string): boolean => parsePlainDate(value) !== null;
const isAnalyticsPeriodPreset = (value: string | undefined): value is AnalyticsPeriodPreset =>
  analyticsPeriodPresets.some((preset) => preset === value);

export function getDefaultAnalyticsFilters(today: Temporal.PlainDate | null): AnalyticsFilters {
  const dateTo = today?.toString() ?? '';

  return {
    dateFrom: today?.with({ day: 1 }).toString() ?? dateTo,
    dateTo,
    locationId: '',
    preset: 'current-month',
  };
}

export function getAnalyticsDateRangeError(
  dateFrom: string,
  dateTo: string,
  today: Temporal.PlainDate | null,
): string | null {
  if (!today) return 'Часовой пояс организации не загружен.';
  if (!isValidIsoDate(dateFrom) || !isValidIsoDate(dateTo)) {
    return 'Укажите обе даты периода.';
  }

  if (dateTo > today.toString()) {
    return 'Дата окончания периода не может быть позже сегодняшнего дня.';
  }

  if (dateFrom > dateTo) {
    return 'Дата начала периода не может быть позже даты окончания.';
  }

  const daysCount = parsePlainDate(dateFrom)!.until(parsePlainDate(dateTo)!).days + 1;

  if (daysCount > 366) {
    return 'Период аналитики не может превышать 366 дней.';
  }

  return null;
}

function isCurrentMonthToDate(dateFrom: string, dateTo: string, today: Temporal.PlainDate | null): boolean {
  const from = parsePlainDate(dateFrom);
  const to = parsePlainDate(dateTo);
  return Boolean(
    today &&
    from &&
    to &&
    from.day === 1 &&
    from.year === today.year &&
    from.month === today.month &&
    to.year === today.year &&
    to.month === today.month &&
    Temporal.PlainDate.compare(from, to) <= 0 &&
    to.day < today.daysInMonth,
  );
}

export function resolveAnalyticsComparisonMode(
  filters: AnalyticsFilters,
  today: Temporal.PlainDate | null,
): AnalyticsComparisonMode {
  if (filters.compare === 'year') {
    return AnalyticsComparisonMode.SamePeriodLastYear;
  }

  if (filters.preset === 'current-month') {
    return AnalyticsComparisonMode.PreviousMonthToDate;
  }

  return filters.preset === 'custom' && isCurrentMonthToDate(filters.dateFrom, filters.dateTo, today)
    ? AnalyticsComparisonMode.PreviousMonthToDate
    : AnalyticsComparisonMode.PreviousPeriod;
}

export function buildAnalyticsQueryPeriod(filters: AnalyticsFilters, today: Temporal.PlainDate | null) {
  return {
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    comparison: resolveAnalyticsComparisonMode(filters, today),
  };
}

/**
 * Фильтры из query-строки; без периода в query или с невалидным периодом
 * применяется `defaultPreset`.
 */
export function parseAnalyticsFilters(
  query: LocationQuery,
  today: Temporal.PlainDate | null,
  defaultPreset: Exclude<AnalyticsPeriodPreset, 'custom'> = 'current-month',
): AnalyticsFilters {
  const flat = queryToRecord(query);
  const compare: AnalyticsFilters['compare'] = flat.compare === 'year' ? 'year' : undefined;
  const dateFrom = flat.dateFrom ?? '';
  const dateTo = flat.dateTo ?? '';

  if (getAnalyticsDateRangeError(dateFrom, dateTo, today)) {
    return buildAnalyticsPreset(
      defaultPreset,
      {
        ...getDefaultAnalyticsFilters(today),
        compare,
        locationId: flat.locationId ?? '',
      },
      today,
    );
  }

  const filters: AnalyticsFilters = {
    compare,
    dateFrom,
    dateTo,
    locationId: flat.locationId ?? '',
    preset: isAnalyticsPeriodPreset(flat.preset) ? flat.preset : 'custom',
  };

  return {
    ...filters,
    preset: resolveAnalyticsPeriodPreset(filters, today),
  };
}

export function buildAnalyticsFilterQuery(filters: AnalyticsFilters): Record<string, string> {
  return {
    ...(filters.compare === 'year' ? { compare: 'year' } : {}),
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    ...(filters.locationId ? { locationId: filters.locationId } : {}),
    ...(filters.preset !== 'custom' ? { preset: filters.preset } : {}),
  };
}

export function buildAnalyticsPreset(
  preset: Exclude<AnalyticsPeriodPreset, 'custom'>,
  filters: AnalyticsFilters,
  today: Temporal.PlainDate | null,
): AnalyticsFilters {
  if (!today) return { ...filters, dateFrom: '', dateTo: '', preset };
  const currentDate = today.toString();
  const ranges: Record<Exclude<AnalyticsPeriodPreset, 'custom'>, () => { dateFrom: string; dateTo: string }> = {
    'current-month': () => ({ dateFrom: today.with({ day: 1 }).toString(), dateTo: currentDate }),
    'last-12-months': () => ({
      dateFrom: today.subtract({ years: 1 }).add({ days: 1 }).toString(),
      dateTo: currentDate,
    }),
    'last-30-days': () => ({ dateFrom: today.subtract({ days: 29 }).toString(), dateTo: currentDate }),
    'last-3-months': () => ({
      dateFrom: today.subtract({ months: 3 }).add({ days: 1 }).toString(),
      dateTo: currentDate,
    }),
    'last-6-months': () => ({
      dateFrom: today.subtract({ months: 6 }).add({ days: 1 }).toString(),
      dateTo: currentDate,
    }),
    'last-7-days': () => ({ dateFrom: today.subtract({ days: 6 }).toString(), dateTo: currentDate }),
    'previous-month': () => {
      const previousMonth = today.subtract({ months: 1 });

      return {
        dateFrom: previousMonth.with({ day: 1 }).toString(),
        dateTo: previousMonth.with({ day: previousMonth.daysInMonth }).toString(),
      };
    },
    today: () => ({ dateFrom: currentDate, dateTo: currentDate }),
    'year-to-date': () => ({ dateFrom: today.with({ month: 1, day: 1 }).toString(), dateTo: currentDate }),
  };
  const { dateFrom, dateTo } = ranges[preset]();

  return {
    ...filters,
    dateFrom,
    dateTo,
    preset,
  };
}

export function resolveAnalyticsPeriodPreset(
  filters: AnalyticsFilters,
  today: Temporal.PlainDate | null,
): AnalyticsPeriodPreset {
  if (filters.preset === 'custom') {
    return 'custom';
  }

  const range = buildAnalyticsPreset(filters.preset, filters, today);

  return filters.dateFrom === range.dateFrom && filters.dateTo === range.dateTo ? filters.preset : 'custom';
}
